import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/services/api";
import type {
  OutlineTemplate,
  OutlineTemplateInput,
  Outlet,
  OutletInput,
  Output,
  OutputPatch,
  OutputType,
  OutputTypeInfo,
} from "./types";

export const outputKeys = {
  all: ["pubcreator", "outputs"] as const,
  types: () => [...outputKeys.all, "types"] as const,
  outlets: (q: string) => [...outputKeys.all, "outlets", q] as const,
  kantataProjectTasks: (kantataProjectId: string) => [...outputKeys.all, "kantata-project-tasks", kantataProjectId] as const,
  project: (projectId: string) => [...outputKeys.all, "project", projectId] as const,
  output: (outputId: string) => [...outputKeys.all, "output", outputId] as const,
  adminOutlets: () => ["pubcreator", "admin", "outlets"] as const,
  adminTemplates: () => ["pubcreator", "admin", "templates"] as const,
};

export interface KantataTask {
  id: string;
  title: string | null;
  description: string | null;
  status: string | null;
  time_estimate_in_minutes: number | null;
  parent_id: string | null;
  workspace_id: string;
}

// ---- Lookups ----

export function useOutputTypes() {
  return useQuery({
    queryKey: outputKeys.types(),
    queryFn: () => api.get<OutputTypeInfo[]>("/api/output-types"),
    staleTime: 5 * 60_000,
  });
}

export function useOutlets(q: string, { enabled = true }: { enabled?: boolean } = {}) {
  const query = q.trim();
  return useQuery({
    queryKey: outputKeys.outlets(query),
    queryFn: () => api.get<Outlet[]>(`/api/outlets?${new URLSearchParams({ q: query, include_inactive: "false" })}`),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useKantataProjectTasks(kantataProjectId?: string | null, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: outputKeys.kantataProjectTasks(kantataProjectId ?? ""),
    queryFn: () => api.get<KantataTask[]>(`/api/master/kantata-projects/${kantataProjectId}/tasks`),
    enabled: enabled && !!kantataProjectId,
  });
}

// ---- Outputs ----

export function useProjectOutputs(projectId: string) {
  return useQuery({
    queryKey: outputKeys.project(projectId),
    queryFn: () => api.get<Output[]>(`/api/projects/${projectId}/outputs`),
    enabled: !!projectId,
  });
}

export function useOutput(outputId: string) {
  return useQuery({
    queryKey: outputKeys.output(outputId),
    queryFn: () => api.get<Output>(`/api/outputs/${outputId}`),
    enabled: !!outputId,
    // The forms are keyed on the version: a background refetch must not swap them mid-edit.
    // Someone else's save surfaces as a 409 when this user saves.
    refetchOnWindowFocus: false,
  });
}

export function useCreateOutput(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { type: OutputType; kantata_task_id: string; kantata_task_title?: string | null; kantata_task_status?: string | null }) =>
      api.post<Output>(`/api/projects/${projectId}/outputs`, body),
    onSuccess: (output) => {
      queryClient.setQueryData(outputKeys.output(output.id), output);
      queryClient.invalidateQueries({ queryKey: outputKeys.project(projectId) });
    },
  });
}

/** PATCH an output. A stale `version` fails with a 409 (`isConflict`); the caller refetches. */
export function useUpdateOutput(outputId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: OutputPatch) => api.patch<Output>(`/api/outputs/${outputId}`, patch),
    onSuccess: (output) => {
      queryClient.setQueryData(outputKeys.output(output.id), output);
      queryClient.invalidateQueries({ queryKey: outputKeys.project(output.project_id) });
    },
  });
}

// ---- Admin ----

export function useAdminOutlets() {
  return useQuery({
    queryKey: outputKeys.adminOutlets(),
    queryFn: () => api.get<Outlet[]>("/api/admin/outlets"),
  });
}

export function useOutletMutations() {
  const queryClient = useQueryClient();
  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: outputKeys.adminOutlets() }),
      queryClient.invalidateQueries({ queryKey: [...outputKeys.all, "outlets"] }),
    ]);
  return {
    create: useMutation({
      mutationFn: (body: OutletInput) => api.post<Outlet>("/api/admin/outlets", body),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: OutletInput }) => api.put<Outlet>(`/api/admin/outlets/${id}`, body),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete<void>(`/api/admin/outlets/${id}`),
      onSuccess: invalidate,
    }),
  };
}

export function useAdminTemplates() {
  return useQuery({
    queryKey: outputKeys.adminTemplates(),
    queryFn: () => api.get<OutlineTemplate[]>("/api/admin/templates"),
  });
}

export function useTemplateMutations() {
  const queryClient = useQueryClient();
  // The output picker previews each type's outline from its active template.
  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: outputKeys.adminTemplates() }),
      queryClient.invalidateQueries({ queryKey: outputKeys.types() }),
    ]);
  return {
    create: useMutation({
      mutationFn: (body: OutlineTemplateInput) => api.post<OutlineTemplate>("/api/admin/templates", body),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: OutlineTemplateInput }) =>
        api.put<OutlineTemplate>(`/api/admin/templates/${id}`, body),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete<void>(`/api/admin/templates/${id}`),
      onSuccess: invalidate,
    }),
  };
}

